import { useEffect, useState } from 'react';
import { api } from '@/api/client';

interface RolePermissions {
  views: string[];
  tabs: string[];
  features: string[];
  api: string[];
}

interface PermissionConfig {
  version: number;
  roles: Record<string, RolePermissions>;
}

const VIEW_LABELS: Record<string, string> = {
  tasks: 'Taken',
  planning: 'Planning',
  project: 'Projectweergave',
  installer: 'Installateur weergave',
  admin: 'Admin paneel',
  opmeting: 'Opmeting',
  vanmarcke: 'Van Marcke rekenblad',
  groepsdata: 'Groepsdata',
};

const TAB_LABELS: Record<string, string> = {
  offerte: 'Offerte',
  planning: 'Planning',
  materiaal: 'Materiaal',
  collectoren: 'Collectoren',
  documenten: 'Documenten',
};

const FEATURE_LABELS: Record<string, string> = {
  csv_upload: 'CSV Upload',
  photo_approve: "Foto's goedkeuren",
  planning_edit: 'Planning bewerken',
  meerwerk_log: 'Meerwerk loggen',
  meerwerk_include_on_po: 'Meerwerk op PO zetten',
  collector_toggle: 'Collector toggles',
  photo_upload: "Foto's uploaden",
  'opmeting:read': 'Opmeting openen',
  'opmeting:write': 'Opmeting opslaan',
  projecten_maken: 'Projecten maken (Administratie)',
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  projectleider: 'Projectleider',
  projectleider_easykit: 'PL EasyKit',
  installer: 'Installateur',
  interventie: 'Interventie',
  easykit: 'EasyKit',
  warehouse: 'Magazijn',
  oplijster: 'Oplijster',
};

const ROLE_ORDER = [
  'admin',
  'projectleider',
  'projectleider_easykit',
  'installer',
  'interventie',
  'easykit',
  'warehouse',
  'oplijster',
];

function getAllKeys(config: PermissionConfig, type: 'views' | 'tabs' | 'features'): string[] {
  const keys = new Set<string>();
  for (const role of Object.values(config.roles)) {
    for (const key of role[type]) {
      keys.add(key);
    }
  }
  // Add all known keys from labels
  const labelMap = type === 'views' ? VIEW_LABELS : type === 'tabs' ? TAB_LABELS : FEATURE_LABELS;
  for (const key of Object.keys(labelMap)) {
    keys.add(key);
  }
  return Array.from(keys);
}

export function AdminView() {
  const [config, setConfig] = useState<PermissionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api<PermissionConfig>('/api/permissions/config')
      .then((data) => {
        setConfig(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Laden mislukt');
        setLoading(false);
      });
  }, []);

  const handleToggle = (role: string, type: 'views' | 'tabs' | 'features', key: string) => {
    if (!config || role === 'admin') return;

    setConfig((prev) => {
      if (!prev) return prev;
      const rolePerms = prev.roles[role];
      if (!rolePerms) return prev;

      const current = rolePerms[type];
      const has = current.includes(key);
      const updated = has ? current.filter((k) => k !== key) : [...current, key];

      return {
        ...prev,
        roles: {
          ...prev.roles,
          [role]: {
            ...rolePerms,
            [type]: updated,
          },
        },
      };
    });
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setSaveMessage(null);
    setError(null);
    try {
      await api<PermissionConfig>('/api/permissions/config', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      setSaveMessage('Opgeslagen');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
          <span className="sr-only">Laden...</span>
        </div>
        <p className="text-td-muted text-sm mt-3">Configuratie laden...</p>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="mx-4 lg:mx-8 bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
        <p className="font-semibold">Fout bij laden</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!config) return null;

  const roles = ROLE_ORDER.filter((r) => r in config.roles);
  const viewKeys = getAllKeys(config, 'views');
  const tabKeys = getAllKeys(config, 'tabs');
  const featureKeys = getAllKeys(config, 'features');

  return (
    <div className="px-4 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-extrabold text-td-navy">Admin Paneel</h1>
          <p className="text-sm text-td-muted mt-0.5">Rechten per rol configureren</p>
        </div>
        <div className="flex items-center gap-3">
          {saveMessage && (
            <span className="text-sm font-semibold text-green-600">{saveMessage}</span>
          )}
          {error && config && (
            <span className="text-sm font-semibold text-red-600">{error}</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-bold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      <PermissionSection
        title="Weergaven"
        keys={viewKeys}
        labels={VIEW_LABELS}
        roles={roles}
        config={config}
        type="views"
        onToggle={handleToggle}
      />

      <PermissionSection
        title="Project Tabs"
        keys={tabKeys}
        labels={TAB_LABELS}
        roles={roles}
        config={config}
        type="tabs"
        onToggle={handleToggle}
      />

      <PermissionSection
        title="Functies"
        keys={featureKeys}
        labels={FEATURE_LABELS}
        roles={roles}
        config={config}
        type="features"
        onToggle={handleToggle}
      />
    </div>
  );
}

function PermissionSection({
  title,
  keys,
  labels,
  roles,
  config,
  type,
  onToggle,
}: {
  title: string;
  keys: string[];
  labels: Record<string, string>;
  roles: string[];
  config: PermissionConfig;
  type: 'views' | 'tabs' | 'features';
  onToggle: (role: string, type: 'views' | 'tabs' | 'features', key: string) => void;
}) {
  return (
    <div className="mb-8">
      <h2 className="text-base font-extrabold text-td-navy mb-3">{title}</h2>
      <div className="rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th scope="col" className="text-left px-4 py-3 text-xs font-bold text-td-muted uppercase tracking-wide sticky left-0 bg-gray-50 z-10">
                Recht
              </th>
              {roles.map((role) => (
                <th key={role} scope="col" className="text-center px-3 py-3 text-xs font-bold text-td-muted uppercase tracking-wide whitespace-nowrap">
                  {ROLE_LABELS[role] ?? role}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key} className="border-t border-gray-100 hover:bg-gray-50/50">
                <td className="px-4 py-2.5 text-td-navy font-medium whitespace-nowrap sticky left-0 bg-white z-10">
                  {labels[key] ?? key}
                </td>
                {roles.map((role) => {
                  const isAdmin = role === 'admin';
                  const checked = isAdmin || config.roles[role]?.[type]?.includes(key) || false;
                  return (
                    <td key={role} className="text-center px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isAdmin}
                        onChange={() => onToggle(role, type, key)}
                        className="w-4 h-4 rounded border-gray-300 text-td-blue focus:ring-td-blue/40 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={`${labels[key] ?? key} voor ${ROLE_LABELS[role] ?? role}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
